import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Switch,
  FlatList,
  Modal as RNModal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Header, FormHeader, ScreenLayout, colors, spacing, typography } from '../components';
import { BusinessUserPicker } from '../components/BusinessUserPicker';
import { DatePickerField } from '../components/DatePickerField';
import { TimePickerField } from '../components/TimePickerField';
import { VisitOverrideEditor, VisitOverrideValues, cleanOverrideGroup } from '../components/VisitOverrideEditor';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService, VisitPriority } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { usePhoneFormGutter } from '../hooks/usePhoneFormGutter';

interface PrefillContact {
  contactId: string;
  name: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

interface ContactSearchOption {
  contactId: string;
  name: string;
  phone?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

const DEBOUNCE_MS = 300;
const CONTACT_PAGE_SIZE = 25;

const PRIORITIES: VisitPriority[] = ['low', 'normal', 'high', 'urgent'];

const CONTACT_NO_LOCATION_ERROR = 'Contact has no location, maps URL, or address — cannot create a visit';

export const VisitCreateScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const phoneFormGutter = usePhoneFormGutter();
  const { navigateTo, routeParams, showNotification, activeTab, setActiveTab } = useAppContext();
  const { t } = useLocale();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const prefillContact: PrefillContact | undefined = routeParams?.prefillContact;

  const [selectedContact, setSelectedContact] = useState<PrefillContact | undefined>(prefillContact);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [contactResults, setContactResults] = useState<ContactSearchOption[]>([]);
  const [isSearchingContacts, setIsSearchingContacts] = useState(false);
  const [isLoadingMoreContacts, setIsLoadingMoreContacts] = useState(false);
  // Confirmed real bug: the old picker requested a flat limit:25 with no
  // offset and no onEndReached — any business with more than one page of
  // contacts could never reach anything past the first batch, regardless
  // of what they typed (a query matching contact #30 with nothing matching
  // in the first 25 would show "no contacts found").
  const [contactHasMore, setContactHasMore] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [assigneeMode, setAssigneeMode] = useState<'user' | 'department'>('user');
  const [assignedUserId, setAssignedUserId] = useState<string | undefined>(undefined);
  const [assignedUserLabel, setAssignedUserLabel] = useState<string | undefined>(undefined);
  const [assignedDepartmentId, setAssignedDepartmentId] = useState<string | undefined>(undefined);
  const [assignedDepartmentLabel, setAssignedDepartmentLabel] = useState<string | undefined>(undefined);
  const [showAssigneePicker, setShowAssigneePicker] = useState(false);

  const [priority, setPriority] = useState<VisitPriority>('normal');
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [expectedDurationMinutes, setExpectedDurationMinutes] = useState('');
  const [createCalendarEvent, setCreateCalendarEvent] = useState(false);

  // Per-visit override of the campaign/service defaults — full field set via
  // the shared VisitOverrideEditor (see its own doc comment for why this is
  // shared with the campaign create/edit screens rather than duplicated).
  const [overrides, setOverrides] = useState<VisitOverrideValues>({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noLocationError, setNoLocationError] = useState(false);

  const mapContact = (c: any): ContactSearchOption => ({
    contactId: String(c._id),
    name: c.profile?.name || c.profile?.phone || '',
    phone: c.profile?.phone,
    address: c.profile?.location?.address,
    latitude: c.profile?.location?.coordinates?.latitude,
    longitude: c.profile?.location?.coordinates?.longitude,
  });

  const searchContacts = useCallback(async (text: string) => {
    setIsSearchingContacts(true);
    try {
      // hasLocationOrAddress — a visit can't be created for a contact with
      // no location at all, so the picker only ever shows selectable
      // contacts instead of letting the user pick one that will fail at
      // submit time (see the noLocationError handling further down, which
      // still stays as a defensive fallback for the rare stale-result case).
      const res = await apiService.getParentContacts({ name: text || undefined, limit: CONTACT_PAGE_SIZE, offset: 0, hasLocationOrAddress: true });
      const raw: any[] = (res.data as any)?.contacts || (Array.isArray(res.data) ? res.data : []) || [];
      setContactResults(raw.map(mapContact));
      setContactHasMore(raw.length === CONTACT_PAGE_SIZE);
    } catch (err) {
      console.warn('[VisitCreate] searchContacts error:', err);
      setContactResults([]);
      setContactHasMore(false);
    } finally {
      setIsSearchingContacts(false);
    }
  }, []);

  const loadMoreContacts = useCallback(async () => {
    if (isLoadingMoreContacts || isSearchingContacts || !contactHasMore) return;
    setIsLoadingMoreContacts(true);
    try {
      const res = await apiService.getParentContacts({
        name: contactQuery || undefined,
        limit: CONTACT_PAGE_SIZE,
        offset: contactResults.length,
        hasLocationOrAddress: true,
      });
      const raw: any[] = (res.data as any)?.contacts || (Array.isArray(res.data) ? res.data : []) || [];
      setContactResults((prev) => [...prev, ...raw.map(mapContact)]);
      setContactHasMore(raw.length === CONTACT_PAGE_SIZE);
    } catch (err) {
      console.warn('[VisitCreate] loadMoreContacts error:', err);
    } finally {
      setIsLoadingMoreContacts(false);
    }
  }, [contactQuery, contactResults.length, contactHasMore, isLoadingMoreContacts, isSearchingContacts]);

  useEffect(() => {
    if (!showContactPicker) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchContacts(contactQuery), contactQuery ? DEBOUNCE_MS : 0);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [contactQuery, showContactPicker, searchContacts]);

  const openContactPicker = () => {
    setContactQuery('');
    setShowContactPicker(true);
  };

  const selectContact = (option: ContactSearchOption) => {
    setSelectedContact({
      contactId: option.contactId,
      name: option.name,
      address: option.address,
      latitude: option.latitude,
      longitude: option.longitude,
    });
    setShowContactPicker(false);
  };

  const handleSubmit = async () => {
    setNoLocationError(false);
    if (!selectedContact) {
      showNotification('warning', t('visits.create.title'), t('visits.create.errors.contactRequired'));
      return;
    }
    if (!assignedUserId && !assignedDepartmentId) {
      showNotification('warning', t('visits.create.title'), t('visits.create.errors.assigneeRequired'));
      return;
    }
    // Mirrors the backend's own createVisit validation (a calendar event
    // can't be created with no date to put it on) — checked here too so
    // the agent gets immediate feedback instead of a round-trip error.
    if (createCalendarEvent && !scheduledDate) {
      showNotification('warning', t('visits.create.title'), t('visits.create.errors.scheduleRequiredForCalendar'));
      return;
    }

    let scheduledFor: string | undefined;
    if (scheduledDate) {
      const [h, m] = scheduledTime.split(':').map(Number);
      const combined = new Date(scheduledDate);
      combined.setHours(h || 0, m || 0, 0, 0);
      scheduledFor = combined.toISOString();
    }

    const payload: Record<string, any> = {
      contactId: selectedContact.contactId,
      priority,
      createCalendarEvent,
    };
    if (assignedUserId) payload.assignedTo = assignedUserId;
    if (assignedDepartmentId) payload.departmentId = assignedDepartmentId;
    if (scheduledFor) payload.scheduledFor = scheduledFor;
    if (expectedDurationMinutes.trim()) payload.expectedDurationMinutes = Number(expectedDurationMinutes) || undefined;
    const locationOverride = cleanOverrideGroup(overrides.location);
    const photosOverride = cleanOverrideGroup(overrides.photos);
    const calendarOverride = cleanOverrideGroup(overrides.calendar);
    if (locationOverride) payload.locationOverride = locationOverride;
    if (photosOverride) payload.photosOverride = photosOverride;
    if (calendarOverride) payload.calendarOverride = calendarOverride;

    setIsSubmitting(true);
    try {
      const res = await apiService.createVisit(payload);
      const created = res?.data;
      const newId = created?._id || created?.id;
      if (newId) {
        navigateTo('visit-detail', undefined, { visitId: newId });
      } else {
        navigateTo('visits');
      }
    } catch (err: any) {
      const message: string = err?.message || '';
      if (message.includes(CONTACT_NO_LOCATION_ERROR) || message.toLowerCase().includes('no location')) {
        setNoLocationError(true);
      } else {
        showNotification('error', t('visits.create.title'), message || t('visits.errors.generic'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={<FormHeader title={t('visits.create.title')} onBack={() => navigateTo('visits')} />}
    >
      <View style={styles.screen}>
      <ScrollView contentContainerStyle={[styles.content, phoneFormGutter]}>
        <View style={styles.formCard}>
          <Text style={styles.sectionTitle}>{t('visits.create.contactSection')}</Text>
          {selectedContact ? (
            <View style={styles.selectedContactBox}>
              <Text style={styles.selectedContactName}>{selectedContact.name}</Text>
              {selectedContact.address ? <Text style={styles.metaText}>{selectedContact.address}</Text> : null}
              {!prefillContact && (
                <TouchableOpacity onPress={openContactPicker} style={styles.changeContactLink}>
                  <Text style={styles.pickerButtonText}>{t('visits.create.changeContact')}</Text>
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <TouchableOpacity style={styles.pickerButton} onPress={openContactPicker}>
              <Text style={styles.pickerButtonText}>{t('visits.create.selectContact')}</Text>
            </TouchableOpacity>
          )}

          {noLocationError && (
            <View style={styles.errorBox}>
              <Ionicons name="warning-outline" size={16} color={colors.status.error as string} />
              <Text style={styles.errorBoxText}>{t('visits.create.errors.contactNoLocation')}</Text>
            </View>
          )}
        </View>

        <View style={styles.formCard}>
          <Text style={styles.sectionTitle}>{t('visits.create.assigneeSection')}</Text>
          <View style={styles.segmentedRow}>
            <TouchableOpacity
              style={[styles.segmentButton, assigneeMode === 'user' && styles.segmentButtonActive]}
              onPress={() => setAssigneeMode('user')}
            >
              <Text style={[styles.segmentButtonText, assigneeMode === 'user' && styles.segmentButtonTextActive]}>
                {t('visits.create.assignToUser')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.segmentButton, assigneeMode === 'department' && styles.segmentButtonActive]}
              onPress={() => setAssigneeMode('department')}
            >
              <Text style={[styles.segmentButtonText, assigneeMode === 'department' && styles.segmentButtonTextActive]}>
                {t('visits.create.assignToDepartment')}
              </Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity style={styles.pickerButton} onPress={() => setShowAssigneePicker(true)}>
            <Text style={styles.pickerButtonText}>
              {assigneeMode === 'user'
                ? (assignedUserLabel || t('visits.create.selectUser'))
                : (assignedDepartmentLabel || t('visits.create.selectDepartment'))}
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.formCard}>
          <Text style={styles.sectionTitle}>{t('visits.create.prioritySection')}</Text>
          <View style={styles.segmentedRow}>
            {PRIORITIES.map((p) => (
              <TouchableOpacity
                key={p}
                style={[styles.segmentButton, priority === p && styles.segmentButtonActive]}
                onPress={() => setPriority(p)}
              >
                <Text style={[styles.segmentButtonText, priority === p && styles.segmentButtonTextActive]}>
                  {t(`visits.priority.${p}` as any)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.formCard}>
          <Text style={styles.sectionTitle}>{t('visits.create.scheduleSection')}</Text>
          <DatePickerField
            label={t('visits.create.scheduledDate')}
            value={scheduledDate}
            onChange={setScheduledDate}
          />
          <View style={{ height: spacing.sm }} />
          <TimePickerField
            label={t('visits.create.scheduledTime')}
            value={scheduledTime}
            onChange={setScheduledTime}
          />
          <Text style={styles.formLabel}>{t('visits.create.expectedDuration')}</Text>
          <TextInput
            style={styles.input}
            keyboardType="number-pad"
            value={expectedDurationMinutes}
            onChangeText={setExpectedDurationMinutes}
            placeholder={t('visits.create.expectedDurationPlaceholder')}
          />
        </View>

        <View style={styles.formCard}>
          <View style={styles.switchRow}>
            <Text style={styles.formLabel}>{t('visits.create.createCalendarEvent')}</Text>
            <Switch
              value={createCalendarEvent}
              onValueChange={setCreateCalendarEvent}
              trackColor={{ true: colors.primary as string, false: colors.border as string }}
              thumbColor={colors.white as string}
            />
          </View>
        </View>

        {/* Per-visit override — full field set via the shared editor. */}
        <View style={styles.formCard}>
          <VisitOverrideEditor value={overrides} onChange={setOverrides} />
        </View>

        <TouchableOpacity
          style={[styles.primaryButton, isSubmitting && styles.disabledButton]}
          onPress={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <ActivityIndicator color={colors.white as string} />
          ) : (
            <Text style={styles.primaryButtonText}>{t('visits.create.submit')}</Text>
          )}
        </TouchableOpacity>
      </ScrollView>
      </View>

      <RNModal visible={showContactPicker} transparent animationType="fade" onRequestClose={() => setShowContactPicker(false)}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={() => setShowContactPicker(false)}>
          <TouchableOpacity activeOpacity={1} style={styles.pickerCard} onPress={() => {}}>
            <Text style={styles.pickerTitle}>{t('visits.create.selectContact')}</Text>
            <TextInput
              style={styles.searchInput}
              placeholder={t('visits.create.searchContacts')}
              value={contactQuery}
              onChangeText={setContactQuery}
              autoCapitalize="none"
            />
            {isSearchingContacts ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator color={colors.primary as string} />
              </View>
            ) : (
              <FlatList
                data={contactResults}
                keyExtractor={(c) => c.contactId}
                style={styles.pickerList}
                keyboardShouldPersistTaps="handled"
                onEndReachedThreshold={0.4}
                onEndReached={loadMoreContacts}
                ListEmptyComponent={<Text style={styles.emptyText}>{t('visits.create.noContactsFound')}</Text>}
                ListFooterComponent={isLoadingMoreContacts ? (
                  <View style={styles.loadingBox}>
                    <ActivityIndicator color={colors.primary as string} size="small" />
                  </View>
                ) : null}
                renderItem={({ item }) => (
                  <TouchableOpacity style={styles.contactOption} onPress={() => selectContact(item)}>
                    <Text style={styles.contactOptionName}>{item.name}</Text>
                    {item.phone ? <Text style={styles.metaText}>{item.phone}</Text> : null}
                  </TouchableOpacity>
                )}
              />
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </RNModal>

      <BusinessUserPicker
        visible={showAssigneePicker}
        onClose={() => setShowAssigneePicker(false)}
        onConfirm={async (ids) => {
          const id = ids[0];
          setShowAssigneePicker(false);
          if (!id) return;
          // BusinessUserPicker's onConfirm only ever returns raw ids —
          // confirmed real bug: the picker button previously displayed that
          // raw id as if it were the label, since nothing here resolved it
          // to the actual selected user/department's name.
          const businessId = apiService.getBusinessIdFromToken();
          if (assigneeMode === 'user') {
            setAssignedUserId(id);
            setAssignedDepartmentId(undefined);
            setAssignedDepartmentLabel(undefined);
            setAssignedUserLabel(id);
            if (businessId) {
              try {
                const res = await apiService.getBusinessUsers(businessId);
                const match = Array.isArray(res.data) ? (res.data as any[]).find((u) => u.userId === id) : undefined;
                if (match) {
                  const first = match.user?.firstName || '';
                  const last = match.user?.lastName || '';
                  setAssignedUserLabel((first || last) ? `${first} ${last}`.trim() : (match.user?.email || id));
                }
              } catch (err) {
                console.warn('[VisitCreate] resolveUserLabel error:', err);
              }
            }
          } else {
            setAssignedDepartmentId(id);
            setAssignedUserId(undefined);
            setAssignedUserLabel(undefined);
            setAssignedDepartmentLabel(id);
            if (businessId) {
              try {
                const res = await apiService.getDepartments(businessId);
                const match = Array.isArray(res.data) ? (res.data as any[]).find((d) => d._id === id) : undefined;
                if (match?.name) setAssignedDepartmentLabel(match.name);
              } catch (err) {
                console.warn('[VisitCreate] resolveDepartmentLabel error:', err);
              }
            }
          }
        }}
        mode="single"
        source={assigneeMode === 'user' ? 'users' : 'departments'}
        title={assigneeMode === 'user' ? t('visits.create.selectUser') : t('visits.create.selectDepartment')}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: { flex: 1 },
  content: { padding: spacing.md, paddingBottom: spacing.xl * 2 },

  formCard: {
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border as string,
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginBottom: spacing.sm,
  },
  formLabel: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary as string,
    backgroundColor: colors.background.surface as string,
    marginBottom: spacing.sm,
  },

  pickerButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  pickerButtonText: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },

  selectedContactBox: { padding: spacing.sm, borderRadius: 8, backgroundColor: colors.background.secondary as string, marginBottom: spacing.sm },
  selectedContactName: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  changeContactLink: { marginTop: spacing.xs, alignSelf: 'flex-start' },
  metaText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string },

  errorBox: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.xs },
  errorBoxText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.status.error as string, flex: 1 },

  segmentedRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  segmentButton: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  segmentButtonActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },
  segmentButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  segmentButtonTextActive: { color: colors.white as string, fontFamily: typography.fontFamily.semibold },

  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  overrideToggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },

  primaryButton: { backgroundColor: colors.primary as string, borderRadius: 12, paddingVertical: spacing.md, alignItems: 'center', marginTop: spacing.md },
  primaryButtonText: { color: colors.white as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  disabledButton: { opacity: 0.6 },

  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', alignItems: 'center', justifyContent: 'center' },
  pickerCard: {
    width: '90%',
    maxWidth: 380,
    maxHeight: '75%',
    backgroundColor: colors.background.surface as string,
    borderRadius: 14,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  pickerTitle: { fontSize: typography.fontSize.xl, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
  searchInput: {
    height: 40,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.background.secondary as string,
    color: colors.text.primary as string,
    fontSize: typography.fontSize.sm,
  },
  loadingBox: { paddingVertical: spacing.lg, alignItems: 'center' },
  pickerList: { maxHeight: 360 },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string, textAlign: 'center', paddingVertical: spacing.lg },
  contactOption: { paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border as string },
  contactOptionName: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary as string },
});
